import Box from "@material-ui/core/esm/Box";
import Radio from "@material-ui/core/esm/Radio";
import RadioGroup from "@material-ui/core/esm/RadioGroup";
import FormControl from "@material-ui/core/esm/FormControl";
import FormControlLabel from "@material-ui/core/esm/FormControlLabel";
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import { getActiveAgreementsForCustomer } from 'api/agreements/getActiveAgreementsForCustomer';
import { Select, Input } from 'components/Form';
import InfoBox from 'components/InfoBox/InfoBox';
import Spinner from 'components/Spinner/Spinner';
import { BoldSubtitle } from 'components/Text';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import { chosenAgreement as chosenAgreementAtom, newAgreementName as newAgreementNameAtom, agreementOption as agreementOptionAtom, AgreementOption } from 'state/agreement';
import { getIdFromAgreement } from 'utils/agreements/isAgreement';
import checkLoadable from 'utils/recoil/checkLoadable';
import useStyles from './ChooseAgreementStyles';
export default function ChooseAgreement({
  transferTag = false
}) {
  const {
    t
  } = useTranslation();
  const {
    customer,
    isBusinessCustomer
  } = useCurrentCustomer();
  const {
    chooseAgreementRoot,
    alignLeft,
    newAgreement
  } = useStyles();
  const agreementsLoadable = useRecoilValueLoadable(getActiveAgreementsForCustomer(customer?.id));
  const {
    contents,
    loading
  } = checkLoadable(agreementsLoadable);
  const agreements = useMemo(() => contents?.results ?? [], [contents?.results]);
  const [chosenAgreement, setChosenAgreement] = useRecoilState(chosenAgreementAtom);
  const [newAgreementName, setNewAgreementName] = useRecoilState(newAgreementNameAtom);
  const defaultItem = getIdFromAgreement(chosenAgreement ?? agreements[0]);
  const [agreementOption, setAgreementOption] = useRecoilState(agreementOptionAtom);
  // Sets invoice reference field name
  useEffect(() => {
    if (loading) return;
    if (agreementOption === AgreementOption.NEW) {
      setNewAgreementName(`${t('agreements.agreement')} ${contents?.totalResults ? contents?.totalResults + 1 : 1}`);
    }
  }, [loading, agreementOption, agreements.length, setNewAgreementName, t, contents?.totalResults]);
  const hasAgreements = agreements && agreements.length > 0;
  useEffect(() => {
    if (loading) return;
    if (hasAgreements) {
      setAgreementOption(AgreementOption.EXISTING);
      setChosenAgreement(prev => prev ?? agreements[0]);
    } else setAgreementOption(AgreementOption.NEW);
  }, [loading, agreements, hasAgreements, setAgreementOption, setChosenAgreement]);
  const handleRadioGroupChange = event => {
    const {
      value
    } = event.target;
    if (value === AgreementOption.NEW) {
      setChosenAgreement(null);
    } else {
      setNewAgreementName('');
      setChosenAgreement(agreements[0]);
    }
    setAgreementOption(value);
  };
  const mappedItems = agreements?.map(({
    id,
    name
  }) => ({
    value: id ?? '',
    label: name ?? ''
  }));
  const handleSelectChange = input => {
    const selectOption = agreements?.find(({
      id
    }) => input === id);
    if (selectOption) setChosenAgreement(selectOption);else setNewAgreementName(input);
  };
  const handleInputChange = async input => {
    setNewAgreementName(input);
  };
  if (loading) {
    return /*#__PURE__*/React.createElement(Spinner, null);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: chooseAgreementRoot
  }, hasAgreements && /*#__PURE__*/React.createElement(BoldSubtitle, null, t('select_agreement.agreement_invoice')), /*#__PURE__*/React.createElement(FormControl, {
    component: "fieldset"
  }, hasAgreements && /*#__PURE__*/React.createElement(Box, {
    my: 2
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    "aria-label": "select agreement option",
    name: "AgreementOption",
    value: agreementOption,
    onChange: handleRadioGroupChange
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    value: AgreementOption.EXISTING,
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('select_agreement.existing_agreement_option')
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    value: AgreementOption.NEW,
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('select_agreement.new_agreement_option')
  }))), /*#__PURE__*/React.createElement("div", {
    className: alignLeft
  }, agreementOption === AgreementOption.EXISTING && hasAgreements && agreements.length > 1 && /*#__PURE__*/React.createElement(Box, {
    mt: 1
  }, /*#__PURE__*/React.createElement(Select, {
    id: "choose-agreement",
    items: mappedItems,
    label: t('select_agreement.select_existing_agreement'),
    onChange: handleSelectChange,
    defaultItem: defaultItem
  })), (agreementOption === AgreementOption.NEW || !hasAgreements) && /*#__PURE__*/React.createElement("div", {
    className: newAgreement
  }, /*#__PURE__*/React.createElement(Input, {
    label: t('select_agreement.select_new_agreement'),
    placeholder: t('agreements.agreement_name'),
    onChange: handleInputChange,
    editable: true,
    error: !newAgreementName,
    helperText: !newAgreementName ? t('select_agreement.empty_name') : ''
  }, newAgreementName), transferTag && isBusinessCustomer && /*#__PURE__*/React.createElement(InfoBox, {
    warning: true
  }, t('select_agreement.transfer_new_agreement'))))));
}